import './types';
// Datadog RUM view tracking: this wrapped createRouter emits normalized SPA
// view names (e.g. `/invoices/:id`, not `/invoices/123`) via a router afterEach
// hook. Pairs with `vuePlugin({ router: true })` in plugins/datadog.ts. It's a
// thin wrapper over vue-router's createRouter and a safe no-op when RUM isn't
// initialized (dev / no creds), so router behavior is otherwise unchanged.
import { createRouter } from '@datadog/browser-rum-vue/vue-router-v4';
import { createWebHistory } from 'vue-router';
import type { RouteLocationNormalized } from 'vue-router';
import { AppShell, PublicShell, WidgetShell } from '@sera/ui';
import { h } from 'vue';
import {
  useAuthStore,
  useTenantStore,
  legacyRedirects,
  toRedirectRoute,
} from '@sera/core';
import { installChunkReloadHandlers, RELOAD_FLAG } from './chunkReload';

// Phase 2 nested route structure: shell layouts wrap child route content via
// <RouterView /> in each shell template. Phase 3 adds beforeEach guards on
// protected routes (meta.requiresAuth: true on AppShell parent).
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      component: AppShell,
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'home',
          component: () => import('../views/HomeView.vue'),
          meta: { depth: 1, title: 'Home' },
        },
        {
          path: 'appointments',
          name: 'appointments',
          component: () => import('../views/AppointmentsView.vue'),
          meta: { depth: 1, title: 'Appointments' },
        },
        // Phase 05.1: Invoice list (BILL-01). Renamed from the `billing` →
        // BillingView stub to `invoices` → InvoicesView (D-01). Lazy AppShell
        // child; auth inherited from the parent's meta.requiresAuth via
        // makeAuthGuard's to.matched.some(...). Phase 5.2 added `/invoices/:id`
        // as a sibling below.
        {
          path: 'invoices',
          name: 'invoices',
          component: () => import('../views/InvoicesView.vue'),
          meta: { depth: 1, title: 'Invoices' },
        },
        // Phase 5.2: Invoice Detail (BILL-02, D-20) — lazy-loaded AppShell
        // child. Auth is inherited from the parent AppShell's
        // `meta.requiresAuth: true` via makeAuthGuard's `to.matched.some(...)`
        // (no per-route requiresAuth needed). Sibling of the `/invoices` list.
        // Email deep-links resolve via the existing auth-guard login redirect.
        {
          path: 'invoices/:id',
          name: 'invoice-detail',
          component: () => import('../views/InvoiceDetailView.vue'),
          meta: { depth: 1, title: 'Invoice' },
        },
        // Phase 05.4: Quote list (BILL-14). Lazy AppShell child; auth is
        // inherited from the parent's meta.requiresAuth via makeAuthGuard's
        // to.matched.some(...). Phase 5.5 adds `/quotes/:id` as a sibling.
        {
          path: 'quotes',
          name: 'quotes',
          component: () => import('../views/QuotesView.vue'),
          meta: { depth: 1, title: 'Quotes' },
        },
        {
          path: 'create-account/success',
          name: 'create-account-success',
          component: () => import('../views/CreateAccountSuccessView.vue'),
          meta: { depth: 1, title: 'Account Created' },
        },
        // Phase 4: Core Appointment Flows — lazy-loaded per CUT-02
        {
          path: 'schedule',
          name: 'schedule',
          component: () => import('../views/ScheduleView.vue'),
          meta: { depth: 1, title: 'Schedule' },
        },
        // Plan 04.6-08: unified canonical appointment-detail view. Route
        // name renamed to `appointment-detail` to match the sketch 022
        // contract; the path stays `/appointment/:id` to preserve every
        // existing caller (AppointmentCard, ScheduleView push, deep
        // links, e2e fixtures). See SUMMARY for the deviation note.
        {
          path: 'appointment/:id',
          name: 'appointment-detail',
          component: () => import('../views/AppointmentDetailView.vue'),
          meta: { depth: 1, title: 'Appointment' },
        },
        // Plan 04.6-08: `/appointment-summary/:id` redirects to the unified
        // detail view; the legacy `appointment-summary` named route is
        // removed. AppointmentSummaryView.vue has been deleted, so this
        // redirect is the only remaining surface for old summary links.
        {
          path: 'appointment-summary/:id',
          redirect: (to) => ({
            name: 'appointment-detail',
            params: { id: to.params.id },
          }),
        },
        // Phase 5.5: Quote Detail (PODZ-1661) — lazy-loaded per CUT-02, D-06.
        // Auth is inherited from the parent AppShell's `meta.requiresAuth: true`
        // via makeAuthGuard's `to.matched.some(...)`. Sibling of `/quotes` list
        // (Plan 05.4-02). 5.5 D-07 / CR-01: back-link target is `/quotes`
        // (the quote-list route shipped in Plan 05.4-02).
        {
          path: 'quotes/:id',
          name: 'quote-detail',
          component: () => import('../views/QuoteDetailView.vue'),
          meta: { depth: 1, title: 'Quote' },
        },
        {
          path: 'profile',
          name: 'profile',
          component: () => import('../views/ProfileView.vue'),
          meta: { depth: 1, title: 'Profile' },
        },
        // Dev-only routes: eliminated by Vite dead code removal in production builds
        ...(import.meta.env.DEV
          ? [
              {
                path: 'dev',
                name: 'dev',
                component: () => import('../views/DevView.vue'),
                meta: { depth: 1, title: 'Dev' },
              },
            ]
          : []),
      ],
    },
    {
      path: '/login',
      component: PublicShell,
      children: [
        {
          path: '',
          name: 'login',
          component: () => import('../views/LoginView.vue'),
          meta: { depth: 1, title: 'Login' },
        },
      ],
    },
    {
      path: '/password-reset',
      component: PublicShell,
      children: [
        {
          path: '',
          name: 'password-reset',
          component: () => import('../views/PasswordResetView.vue'),
          meta: { depth: 1, title: 'Password Reset' },
        },
      ],
    },
    {
      path: '/new-password/:token',
      component: PublicShell,
      children: [
        {
          path: '',
          name: 'new-password',
          component: () => import('../views/NewPasswordView.vue'),
          meta: { depth: 1, title: 'New Password' },
        },
      ],
    },
    {
      path: '/create-account',
      component: PublicShell,
      children: [
        {
          path: '',
          name: 'create-account',
          component: () => import('../views/CreateAccountView.vue'),
          meta: { depth: 1, title: 'Create Account' },
        },
      ],
    },
    // Phase 4: Magic link appointment view — PublicShell wide variant, no
    // auth (APPT-11). 260524-lo7 (G4): swapped from TokenShell to PublicShell
    // per the single-shell decision in legacy-magic-link-presentation.md §10.4
    // D5. The `wide` PublicShell prop bumps the card max-width to 720px so the
    // appointment content + inline tracking surface from sketch 022 fits.
    {
      path: '/appointment-details/:token?',
      component: {
        name: 'PublicShellWide',
        render: () => h(PublicShell, { wide: true }),
      },
      meta: { requiresAuth: false },
      children: [
        {
          path: '',
          name: 'appointment-details',
          component: () => import('../views/MagicLinkAppointmentView.vue'),
          meta: { depth: 1, title: 'Appointment' },
        },
      ],
    },
    // Phase 8: Public pay-by-link surface — PublicShell wide variant, no auth
    // (PAYLINK-01 / PAYLINK-15, D-02). Clones the `/appointment-details/:token?`
    // record verbatim (same PublicShellWide render-fn wrapper +
    // meta.requiresAuth:false + nested child) so makeAuthGuard's
    // `explicitlyPublic` branch (to.matched.some(r => r.meta?.requiresAuth ===
    // false)) short-circuits ALL auth logic. There is deliberately NO
    // beforeEnter and NO redirect: a flag-off or bad token renders PayView's
    // in-view privacy state, it must NEVER bounce to /login (D-02/D-10). No
    // `?redirect=` param is ever built on this route.
    {
      path: '/pay/:token?',
      component: {
        name: 'PublicShellWide',
        render: () => h(PublicShell, { wide: true }),
      },
      meta: { requiresAuth: false },
      children: [
        {
          path: '',
          name: 'pay',
          component: () => import('../views/pay/PayView.vue'),
          meta: { depth: 1, title: 'Pay' },
        },
      ],
    },
    // Public embedded schedule widget (legacy parity). D-28 updated 2026-05-20:
    // moved out of AppShell so unauthenticated marketing-site visitors can book
    // without logging in.
    {
      path: '/schedule-appointment',
      component: WidgetShell,
      meta: { requiresAuth: false },
      children: [
        {
          path: '',
          name: 'schedule-appointment',
          component: () => import('../views/EmbeddedScheduleView.vue'),
          meta: { depth: 1, title: 'Schedule Appointment' },
        },
      ],
    },
    // PODZ-1757 / CUT-06: legacy Vue 2 -> Vue 3 redirect records, generated
    // from the shared `legacyRedirects` array in @sera/core (the e2e walk spec
    // imports the SAME array, so it can never drift from these routes). Each
    // entry is an absolute top-level path (e.g. /invoice/:id), so they sit as
    // siblings of the shell records and need no component/meta — the function
    // redirect forwards params + query and the auth guard runs against the
    // resolved target. NOTE: the existing `/appointment-summary/:id` (HYPHEN)
    // precedent above is intentionally left untouched; the new
    // `/appointment_summary/:id` (UNDERSCORE) entry comes from the shared array.
    // MUST stay BEFORE the catch-all so legacy paths win over it.
    ...legacyRedirects.map(toRedirectRoute),
    // Catch-all: redirect any unmatched path to home. Required for FOUND-09
    // (CloudFront 403/404 -> /index.html rewrite delivers the SPA, then the
    // router must resolve to a valid route).
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
});

/**
 * makeAuthGuard — extracted for testability (guard.spec.ts calls it directly).
 *
 * Rules:
 * 1. Routes with explicit meta.requiresAuth=false (e.g. /appointment-details/:token) skip all auth logic.
 * 2. Protected routes (meta.requiresAuth=true via AppShell parent) redirect
 *    unauthenticated users to /login?redirect=<fullPath>.
 * 3. Authenticated users hitting /login are bounced to / (or redirect query param).
 * 4. All other routes (PublicShell siblings with no meta.requiresAuth) pass through.
 */
export function makeAuthGuard(
  getAuthStore: () => { isAuthenticated: boolean },
) {
  return (to: RouteLocationNormalized, _from: RouteLocationNormalized) => {
    const auth = getAuthStore();
    const requiresAuth = to.matched.some((r) => r.meta?.requiresAuth === true);
    const explicitlyPublic = to.matched.some(
      (r) => r.meta?.requiresAuth === false,
    );

    // Public-marked routes (e.g. /appointment-details/:token) skip all auth logic
    if (explicitlyPublic) return true;

    // Protected route + unauthenticated → /login with redirect param
    if (requiresAuth && !auth.isAuthenticated) {
      return {
        name: 'login' as const,
        query: { redirect: to.fullPath },
      };
    }

    // Authenticated user navigating to /login: bounce to home or to redirect param.
    // Only accept relative, non-protocol-relative paths to prevent open redirect.
    if (to.name === 'login' && auth.isAuthenticated) {
      // CR-03: tighten the relative-path check to reject backslash-prefixed
      // variants (`/\\foo`, `/\/evil.example`) that some browsers normalise
      // to `//evil.example` at navigation time, in addition to the canonical
      // protocol-relative `//evil.example` attack. Also reject empty and
      // non-string inputs explicitly so the function is total over its input
      // type (Vue Router already gives us `string | string[] | null`).
      const isRelativePath = (s: string): boolean => {
        if (typeof s !== 'string' || s.length === 0) return false;
        if (s[0] !== '/') return false;
        // Second character must NOT be a path-separator-like — these are
        // the protocol-relative (`//`) and backslash-normalised (`/\`)
        // variants the browser may re-shape into an authority component.
        if (s[1] === '/' || s[1] === '\\') return false;
        return true;
      };
      const redirectQuery =
        typeof to.query.redirect === 'string' &&
        isRelativePath(to.query.redirect)
          ? to.query.redirect
          : null;
      return redirectQuery || '/';
    }

    return true;
  };
}

/**
 * makeSchedulingGuard — redirect away from the booking wizard when the tenant
 * has disabled customer self-scheduling (PODZ-1835 / AC1). Kept a SEPARATE
 * exported factory (does not touch makeAuthGuard) so guard.spec.ts can unit-test
 * it directly.
 *
 * The header + dashboard CTAs already route to the call-office experience when
 * scheduling is off; this is the belt-and-suspenders for direct-URL entry (typed
 * `/schedule`, a bookmark, or a stale link) so the customer is NEVER dropped on
 * the now-hidden booking wizard. Client-side UX gate only — the booking widget
 * and server remain the source of truth.
 *
 * PODZ-1985: the guard is ASYNC and awaits tenant config readiness on a COLD
 * `/schedule` navigation. `isSchedulingAllowed` is intentionally default-ON while
 * `tenantStore.config` is null (PODZ-1835 AC6 — DO NOT change that semantic). On
 * a cold load the initial-navigation guard runs BEFORE App.vue's onBeforeMount
 * `await tenantStore.init()` resolves, so reading the getter synchronously saw a
 * null config → let the wizard mount and never re-ran. We now `await store.init()`
 * when `!appReady` before reading the gate; `init()` is memoized so this shares
 * App.vue's single boot (no duplicate fetch). In-app navigation (config already
 * resolved → `appReady` true) skips the await entirely — unchanged.
 *
 * Stale-config re-fetch: `init()` is memoized and never re-fetches, so a partner
 * that disables self-scheduling mid-session would keep resolving the stale
 * `isSchedulingAllowed`. Every navigation to `/schedule` (from the header /
 * dashboard / first-time CTAs, all `<RouterLink to="/schedule">`) therefore
 * `await store.refresh()`es the tenant config before reading the gate, so the
 * click sees the current flag. The refresh is fail-open: a rejected fetch keeps
 * the last-known config (default-ON semantics) rather than stranding the customer
 * on a network blip.
 *
 * Scope note: the same null-config default-ON race does NOT affect
 * reschedule/cancel — those are component-level gates in the appointment Manage
 * popover, which only render after App.vue's `appReady` RouterView gate, so there
 * is no direct-URL cold-load entry point for them. Out of scope per the AC.
 */
export function makeSchedulingGuard(
  getTenantStore: () => {
    appReady: boolean;
    isSchedulingAllowed: boolean;
    init: () => Promise<void>;
    refresh: () => Promise<void>;
  },
) {
  return async (
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
  ) => {
    // Short-circuit non-schedule routes FIRST: they never touch the store or
    // await, preserving in-app perf and the "never touches non-schedule routes"
    // guarantee.
    if (to.name !== 'schedule') return true;

    const store = getTenantStore();
    if (!store.appReady) {
      try {
        await store.init();
      } catch {
        // Let navigation resolve to `true`; App.vue's tenant-load-failed screen
        // + the `appReady` RouterView gate prevent the wizard from mounting.
        return true;
      }
    }
    // Re-fetch fresh so a scheduling toggle flipped server-side AFTER the
    // memoized boot is honored on THIS click. Fail-open: a rejected refresh
    // keeps the last-known config (default-ON) — never strand the customer on
    // a network blip.
    try {
      await store.refresh();
    } catch {
      /* keep last-known config */
    }
    return store.isSchedulingAllowed ? true : { name: 'home' as const };
  };
}

router.beforeEach(makeAuthGuard(() => useAuthStore()));
router.beforeEach(makeSchedulingGuard(() => useTenantStore()));

router.afterEach((to) => {
  const title = to.meta.title;
  const tenantName = useTenantStore().config?.tenantName;
  document.title = title
    ? tenantName
      ? `${tenantName} - ${title}`
      : title
    : 'Sera';

  window.gtag?.('event', 'page_view', {
    page_path: to.fullPath,
    page_title: document.title,
  });
});

// Recover from stale-chunk dynamic-import failures after a deploy (the previous
// build's hashed chunks are removed by `aws s3 sync --delete`). See chunkReload.ts.
installChunkReloadHandlers(router, {
  getFlag: () => sessionStorage.getItem(RELOAD_FLAG),
  setFlag: () => sessionStorage.setItem(RELOAD_FLAG, '1'),
  clearFlag: () => sessionStorage.removeItem(RELOAD_FLAG),
  navigate: (path) =>
    path ? window.location.assign(path) : window.location.reload(),
});

export default router;
